/*
 * /patient/appointments — patient-facing appointments list (read-only)
 */
import { cn, Container, Page, PageHeader, Placeholder } from "@emr/react-ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { CalendarClock, Video } from "lucide-react"
import { trpc } from "../../trpc"
import { rolePageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/patient/appointments")({
	component: PatientAppointmentsPage,
	loader: rolePageLoader(["patient"]),
})

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
	scheduled:  { label: "Scheduled",  className: "bg-blue-100  text-blue-700  border-blue-200"  },
	confirmed:  { label: "Confirmed",  className: "bg-green-100 text-green-700 border-green-200" },
	cancelled:  { label: "Cancelled",  className: "bg-red-100   text-red-600   border-red-200"   },
	completed:  { label: "Completed",  className: "bg-gray-100  text-gray-600  border-gray-200"  },
	no_show:    { label: "No Show",    className: "bg-amber-100 text-amber-700 border-amber-200" },
}

function PatientAppointmentsPage() {
	const navigate = useNavigate()

	const { data: patient } = trpc.patient.getByFilter.useQuery({ where: { active: true } })
	const { data: session } = trpc.telemedicine.getActiveByPatient.useQuery(
		{ patientId: patient?.id ?? "" },
		{ enabled: !!patient?.id, refetchInterval: 10000 },
	)

	const { data: appointments = [], isLoading } = trpc.appointment.getAllByFilter.useQuery(
		{ where: { patientId: patient?.id ?? "", active: true } },
		{ enabled: !!patient?.id },
	)

	const { data: practitioners = [] } = trpc.practitioner.getAllByFilter.useQuery(
		{ where: { active: true } },
		{ enabled: !!patient?.id },
	)

	const sorted = [...(appointments ?? [])].sort(
		(a, b) => new Date(b.startsAt as any).getTime() - new Date(a.startsAt as any).getTime(),
	)

	return (
		<Page>
			<Container>
				<div className="flex items-center justify-between mb-2">
					<PageHeader title="My Appointments" />
					{session && (
						<button
							onClick={() => navigate({ to: "/patient/session" })}
							className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-500 transition-colors animate-pulse"
						>
							<Video className="w-4 h-4" />
							Join Video Visit
						</button>
					)}
				</div>

				{isLoading ? (
					<Placeholder title="Loading appointments…" />
				) : sorted.length === 0 ? (
					<Placeholder title="No appointments on record" />
				) : (
					<div className="flex flex-col gap-3">
						{sorted.map((appt) => {
							const prac = (practitioners ?? []).find((p) => p.id === appt.practitionerId)
							const badge = STATUS_BADGE[appt.status] ?? STATUS_BADGE.scheduled
							return (
								<div
									key={appt.id}
									className="flex items-start justify-between gap-4 rounded-lg border bg-card px-5 py-4"
								>
									<div className="flex items-start gap-3">
										<CalendarClock className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
										<div className="flex flex-col gap-1">
											<p className="text-sm font-medium">
												{formatApptTime(appt.startsAt, appt.endsAt, appt.timezone)}
											</p>
											{prac && (
												<p className="text-xs text-muted-foreground">
													{prac.firstName} {prac.lastName}
													{prac.professionalDesignation ? `, ${prac.professionalDesignation}` : ""}
												</p>
											)}
											{appt.type && (
												<p className="text-xs text-muted-foreground capitalize">
													{appt.type.replace("_", " ")}
												</p>
											)}
											{appt.reason && (
												<p className="text-xs italic text-muted-foreground">"{appt.reason}"</p>
											)}
										</div>
									</div>
									<span
										className={cn(
											"inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold",
											badge.className,
										)}
									>
										{badge.label}
									</span>
								</div>
							)
						})}
					</div>
				)}
			</Container>
		</Page>
	)
}

function formatApptTime(startsAt: Date | string, endsAt: Date | string, tz: string): string {
	const s = new Date(startsAt as any)
	const e = new Date(endsAt as any)
	const date = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz, weekday: "short", month: "short", day: "numeric", year: "numeric",
	}).format(s)
	const start = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true,
	}).format(s)
	const end = new Intl.DateTimeFormat("en-CA", {
		timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short",
	}).format(e)
	return `${date} · ${start} – ${end}`
}
